import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { decodeJwtPayload } from "src/utils/auth/jwtClaims";
import { captureError } from "src/utils/errorReporting";
import { fetchImpersonationFavorites, addImpersonationFavorite, removeImpersonationFavorite } from "src/utils/auth/impersonateApi";

function mmss(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// Sticky-ish top bar shown while a betoni.online staffer impersonates a Jerry
// customer: who + a mm:ss countdown to the 10-min token expiry, "Jatka 10 min"
// (extend) and "Lopeta" (return to admin). On expiry it auto-returns.
export default function ImpersonationBanner() {
  const { isImpersonating, user, token, adminToken, returnToAdmin, extendImpersonation } = useAuth();
  const t = useT();
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const autoReturnedRef = useRef(false);

  // Whether the currently-impersonated target is saved to this app's quick-select
  // (personContactTypeId=2). null = not yet known / no target. The favorites
  // endpoints need the admin token, not the active imp token.
  const targetPersonId = user?.person?.[0]?.personId ?? null;
  const [isFav, setIsFav] = useState(null);
  const [favBusy, setFavBusy] = useState(false);

  useEffect(() => {
    if (!isImpersonating) return undefined;
    autoReturnedRef.current = false;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isImpersonating]);

  const expSec = useMemo(() => decodeJwtPayload(token)?.exp ?? 0, [token]);
  const expMs = expSec * 1000;
  const remaining = expMs - now;

  useEffect(() => {
    if (isImpersonating && expMs && remaining <= 0 && !autoReturnedRef.current) {
      autoReturnedRef.current = true;
      returnToAdmin("timeout").catch((err) => captureError(err, { tags: { feature: "auth", operation: "impersonate-auto-return" } }));
    }
  }, [isImpersonating, expMs, remaining, returnToAdmin]);

  // Resolve whether this target is already a quick-select favorite. All setState
  // stays inside run() — the react-hooks/set-state-in-effect workaround.
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isImpersonating || !adminToken || !targetPersonId) { if (!cancelled) setIsFav(null); return; }
      try {
        const favs = await fetchImpersonationFavorites(adminToken);
        if (!cancelled) setIsFav(favs.some((f) => f.contactPersonId === targetPersonId));
      } catch (err) {
        captureError(err, { tags: { feature: "auth", operation: "impersonate-favorites-load" } });
      }
    }
    run();
    return () => { cancelled = true; };
  }, [isImpersonating, adminToken, targetPersonId]);

  const onToggleFav = useCallback(async () => {
    if (!adminToken || !targetPersonId || favBusy) return;
    setFavBusy(true);
    const next = !isFav;
    setIsFav(next); // optimistic
    try {
      if (next) await addImpersonationFavorite({ token: adminToken, targetPersonId });
      else await removeImpersonationFavorite({ token: adminToken, targetPersonId });
    } catch (err) {
      setIsFav(!next); // revert on failure
      captureError(err, { tags: { feature: "auth", operation: "impersonate-favorites-toggle" } });
    } finally {
      setFavBusy(false);
    }
  }, [adminToken, targetPersonId, isFav, favBusy]);

  if (!isImpersonating) return null;

  const targetName =
    [user?.person?.[0]?.personFirstName, user?.person?.[0]?.personLastName].filter(Boolean).join(" ") ||
    user?.person?.[0]?.personEmail ||
    t("auth.impersonateBanner.unknownUser");

  const onExtend = async () => {
    setBusy(true);
    try { await extendImpersonation(); } catch (err) { captureError(err, { tags: { feature: "auth", operation: "impersonate-extend" } }); }
    finally { setBusy(false); }
  };
  const onStop = async () => {
    setBusy(true);
    try { await returnToAdmin(); } catch (err) { captureError(err, { tags: { feature: "auth", operation: "impersonate-stop" } }); }
    finally { setBusy(false); }
  };

  return (
    // zIndex lift: App's shell forces every direct child to zIndex:1, so the
    // later-in-DOM <Container> (and its TilaaBackdrop `position:fixed` background
    // layers, used by /tarjouspyynnot, /valut, /hallinta…) would paint over this
    // banner. Match the AppBar's lift so the banner stays visible on every route.
    <Box sx={{ position: "relative", zIndex: (theme) => theme.zIndex.appBar, bgcolor: "warning.main", color: "warning.contrastText", px: 2, py: 0.75 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {t("auth.impersonateBanner.statusLine", { name: targetName, time: mmss(remaining) })}
        </Typography>
        {adminToken && targetPersonId && isFav !== null && (
          <IconButton
            size="small"
            color="inherit"
            onClick={onToggleFav}
            disabled={favBusy}
            aria-label={isFav ? t("auth.shared.removeFavorite") : t("auth.shared.addFavorite")}
            title={isFav ? t("auth.shared.removeFavorite") : t("auth.shared.addFavorite")}
          >
            {isFav ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
        )}
        <Button size="small" variant="outlined" color="inherit" onClick={onExtend} disabled={busy}>{t("auth.impersonateBanner.extendButton")}</Button>
        <Button size="small" variant="contained" color="inherit" onClick={onStop} disabled={busy} sx={{ color: "warning.main" }}>{t("auth.impersonateBanner.stopButton")}</Button>
      </Stack>
    </Box>
  );
}
